import { FC, useMemo, useState } from "react"
import classNames from "classnames"
import { useNavigate } from "react-router"
import Loader from "@/components/Loader"
import Button, { ButtonIconPosition } from "@/components/shared/Button"
import GridItem from "@/components/shared/Grid/GridItem"
import { Icon, IconColor } from "@/components/shared/Icon"
import { SpinnerSize } from "@/components/shared/Spinner"
import Tag from "@/components/shared/Tag"
import TextInput from "@/components/shared/TextInput"
import { TestId } from "@/constants/globals"
import { ButtonVariant, ColorVariant, Variant } from "@/types/index"

interface ControlsProps {
   isVitalAlert: boolean
   isAlertEnabled: boolean
   title: string
   loading: boolean
   onSave: (newTitle: string) => void
}

const Controls: FC<ControlsProps> = ({
   isVitalAlert,
   isAlertEnabled,
   title,
   loading,
   onSave,
}) => {
   const navigate = useNavigate()
   const [text, setText] = useState(title)
   const [isEditing, setIsEditing] = useState(false)
   const isFormValid = useMemo(() => {
      return title !== text && text.trim() !== ""
   }, [text, title])

   const handleEdit = () => {
      setText(title)
      setIsEditing(true)
   }

   const handleSave = () => {
      onSave(text)
      setIsEditing(false)
   }

   const renderActions = () => {
      return isEditing ? (
         <>
            <Icon
               name="cross"
               size="16"
               color={IconColor.HONOLULU_BLUE}
               onClick={() => setIsEditing(false)}
               data-testid={TestId.ALERT_SETTING_CANCEL_ICON}
            />
            <Icon
               name="save"
               size="16"
               disabled={!isFormValid}
               color={IconColor.HONOLULU_BLUE}
               onClick={handleSave}
               data-testid={TestId.ALERT_SETTING_SAVE_ICON}
            />
         </>
      ) : (
         <Icon
            name="edit"
            size="16"
            color={IconColor.HONOLULU_BLUE}
            onClick={handleEdit}
            data-testid={TestId.ALERT_SETTING_EDIT_ICON}
         />
      )
   }

   return (
      <GridItem
         xs={12}
         sm={12}
         md={5}
         lg={5}
      >
         <div className="alert-setting-detail__controls">
            <Button
               className="alert-setting-detail__back-button"
               iconName="arrowLeft"
               iconPosition={ButtonIconPosition.LEFT}
               variant={ButtonVariant.LARGE}
               onClick={() => navigate(-1)}
            />
            <div className="alert-setting-detail__title">
               {loading ? (
                  <Loader
                     size={SpinnerSize.SMALL}
                     fullHeight={false}
                  />
               ) : isEditing ? (
                  <>
                     <div className="alert-setting-detail__title-label--editing">
                        Alarm name
                     </div>
                     <div className="alert-setting-detail__alert-name-wrapper">
                        <TextInput
                           variant={Variant.LARGE}
                           name="alertTitle"
                           value={text}
                           onChange={(e) => setText(e.target.value)}
                        />
                        {renderActions()}
                     </div>
                  </>
               ) : (
                  <>
                     <div className="alert-setting-detail__title-label">
                        Alarm
                     </div>
                     <div className="alert-setting-detail__alert-name-wrapper">
                        <h2
                           className={classNames({
                              "alert-setting-detail__alert-name--dimmed":
                                 !isAlertEnabled,
                           })}
                        >
                           {title}
                        </h2>
                        {!isVitalAlert && (
                           <Tag
                              label="System"
                              variant={ColorVariant.HONOLULU_BLUE_DARK}
                           />
                        )}
                        {!isAlertEnabled && (
                           <Tag
                              outlined
                              label="Disabled"
                              variant={ColorVariant.CERISE}
                              iconName="snoozeAlert"
                              iconColor={IconColor.CERISE}
                           />
                        )}
                        {isVitalAlert && isAlertEnabled && renderActions()}
                     </div>
                  </>
               )}
            </div>
         </div>
      </GridItem>
   )
}

export default Controls
